/**
 * V13.14.6 — Tier-aware learner badge.
 *
 * Replaces the legacy "Gold" Crown badge with an "Elite" rose-amber gradient
 * + Sparkles glyph. Renders consistently across LearnerLayout, admin
 * UserManagement, UserDetailModal, ReferAFriend, etc.
 *
 * Backward-compat: `tier === 'gold'` is treated as Elite (legacy data).
 *
 * Design decisions:
 *   - Rose→amber gradient signals warmth + premium without the cliché Crown
 *   - Sparkles icon reads as "sparkly" without leaning into royalty/luxury
 *   - Subtle shadow + ring on hover for tactile feel; no overdone glow
 */
import { Sparkles } from 'lucide-react';
import { Badge } from './ui/badge';

const TIER_COPY = {
  elite: 'Elite',
  trial_paid: 'Paid Trial',
  free: 'Free',
  gynae_consult: 'Gynae Consult',
};

export function isEliteTier(tier) {
  // Legacy `gold` rows still exist in production until the migration is run.
  return tier === 'elite' || tier === 'gold';
}

/**
 * <TierBadge tier="elite" />              → Elite badge
 * <TierBadge tier="gold" />               → Elite badge (legacy)
 * <TierBadge tier="trial_paid" />         → Paid Trial outline
 * <TierBadge tier="free" />               → no render (returns null)
 * <TierBadge tier="elite" size="sm" />    → smaller variant
 */
export function TierBadge({ tier, size = 'md', className = '', ...rest }) {
  const isElite = isEliteTier(tier);
  const sizing = size === 'sm'
    ? 'text-[9px] px-1.5 py-0 h-4 gap-0.5'
    : 'text-[10px] px-2 py-0.5 h-5 gap-1';

  if (isElite) {
    return (
      <Badge
        data-testid="tier-badge-elite"
        className={`elite-badge font-medium border-0 inline-flex items-center ${sizing} ${className}`}
        {...rest}
      >
        <Sparkles className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        Elite
      </Badge>
    );
  }
  if (tier === 'trial_paid') {
    return (
      <Badge
        data-testid="tier-badge-trial-paid"
        variant="outline"
        className={`bg-amber-50 text-amber-800 border-amber-200 ${sizing} ${className}`}
        {...rest}
      >
        Paid Trial
      </Badge>
    );
  }
  if (tier === 'gynae_consult') {
    return (
      <Badge
        data-testid="tier-badge-consult"
        variant="outline"
        className={`bg-violet-50 text-violet-800 border-violet-200 ${sizing} ${className}`}
        {...rest}
      >
        {TIER_COPY[tier]}
      </Badge>
    );
  }
  return null; // free / null → no badge
}

/** Small ring style applied to learner avatars to indicate Elite status. */
export function eliteAvatarRing(tier) {
  return isEliteTier(tier)
    ? 'ring-2 ring-rose-400 ring-offset-1 ring-offset-amber-50'
    : '';
}

/** Avatar fallback class applied for Elite — soft rose-amber tint. */
export function eliteAvatarFallback(tier, fallbackClass = 'bg-primary/10 text-primary') {
  return isEliteTier(tier)
    ? 'bg-gradient-to-br from-rose-50 to-amber-50 text-rose-700'
    : fallbackClass;
}

export default TierBadge;
